import { AfterViewInit, Directive, ElementRef, EventEmitter, HostListener, Input, NgZone, Output } from '@angular/core';
import { CellEditorService } from './cell-editor.service';

@Directive({
    selector: '[matCellEditor]',
})
export class CellEditorDirective implements AfterViewInit {

    @Input() public editableColumnDisabled: boolean;

    @Output() public cellOpened: EventEmitter<void> = new EventEmitter<void>();
    @Output() public cellClosed: EventEmitter<void> = new EventEmitter<void>();
    @Output() public cellCanceled: EventEmitter<void> = new EventEmitter<void>();

    constructor(
        private myNgZone: NgZone,
        public myElementRef: ElementRef,
        private myCellEditorService: CellEditorService,
    ) {
    }

    @HostListener('click', [ '$event' ])
    public onClick(_event: MouseEvent): void {
        if (this.editableColumnDisabled !== true) {
            this.myCellEditorService.editingCellClick = true;

            if (this.myCellEditorService.editingCell != null) {
                if (this.myCellEditorService.editingCell !== this.myElementRef.nativeElement && this.myCellEditorService.isEditingCellValid()) {
                    this.closeEditingCell(true);
                    setTimeout(() => {
                        this.openCell();
                    });
                }
            } else {
                this.openCell();
            }
        }
    }

    @HostListener('keydown.enter', [ '$event' ])
    public onEnterKeyDown(event: KeyboardEvent): void {
        if (this.editableColumnDisabled !== true) {
            if (this.myCellEditorService.isEditingCellValid()) {
                this.moveToNextRowCell(event);
            } else {
                event.preventDefault();
            }
        }
    }

    @HostListener('keydown.escape', [ '$event' ])
    public onEscapeKeyDown(event: KeyboardEvent): void {
        if (this.editableColumnDisabled !== true) {
            this.closeEditingCell(false);
            event.preventDefault();
        }
    }

    @HostListener('keydown.tab', [ '$event' ])
    @HostListener('keydown.shift.tab', [ '$event' ])
    @HostListener('keydown.meta.tab', [ '$event' ])
    public onShiftKeyDown(event: KeyboardEvent): void {
        if (this.editableColumnDisabled !== true) {
            if (event.shiftKey) {
                this.moveToCell(event, false);
            } else {
                this.moveToCell(event, true);
            }
        }
    }

    public ngAfterViewInit(): void {
        if (this.editableColumnDisabled !== true) {
            this.myElementRef.nativeElement.classList.add('editable-column');
        }
    }

    private openCell(): void {
        this.cellOpened.emit();
        this.myCellEditorService.updateEditingCell(this.myElementRef.nativeElement, this.cellClosed);
        this.myElementRef.nativeElement.classList.add('editing-cell');
        this.myNgZone.runOutsideAngular(() => {
            setTimeout(() => {
                const focusableInput: HTMLInputElement | HTMLTextAreaElement = this.myElementRef.nativeElement.querySelector('input, textarea');
                if (focusableInput != null) {
                    focusableInput.select();
                } else {
                    const focusableSelect: HTMLElement = this.myElementRef.nativeElement.querySelector('mat-select');
                    if (focusableSelect != null) {
                        focusableSelect.focus();
                    }
                }
            }, 50);
        });
    }

    private closeEditingCell(completed: boolean): void {
        if (completed) {
            // If this method was called from cellClick event, then we would emit the new cell to be closed, not the old one
            // To avoid this, emit cell closed event from the stored emitter in table service
            this.myCellEditorService.cellClosedEmitter.emit();
        } else {
            this.cellCanceled.emit();
        }
        this.myCellEditorService.editingCell.classList.remove('editing-cell');
        this.myCellEditorService.editingCell = null;
        this.myCellEditorService.unbindDocumentEditListener();
    }

    private moveToNextRowCell(event: KeyboardEvent): void {
        const currentCell: Element = this.findCell(event.target as Element);
        if (currentCell == null) {
            return;
        }

        let columnDefClass: string = '';
        currentCell.classList.forEach((className: string): void => {
            const found: boolean = className.indexOf('mat-column-') > -1;
            if (found) {
                columnDefClass = className;
            }
        });

        const targetCell: Element = this.findNextRowEditableColumn(currentCell.parentElement, columnDefClass);
        this.closeEditingCell(true);
        this.executeMove(event, targetCell);
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private moveToCell(event: any, nextCell: boolean): void {
        const currentCell: Element = this.findCell(event.target);
        if (currentCell != null) {
            const targetCell: Element = nextCell ? this.findNextEditableColumn(currentCell) : this.findPreviousEditableColumn(currentCell);
            if (this.myCellEditorService.isEditingCellValid()) {
                this.closeEditingCell(true);
            }
            this.executeMove(event, targetCell);
        }
    }

    private findNextEditableColumn(cell: Element): Element {
        let nextCell: Element = cell.nextElementSibling;

        if (!nextCell) {
            const nextRow: Element = cell.parentElement.nextElementSibling;
            if (nextRow) {
                nextCell = nextRow.firstElementChild;
            }
        }

        if (nextCell) {
            return nextCell.classList.contains('editable-column') ? nextCell : this.findNextEditableColumn(nextCell);
        } else {
            return null;
        }
    }

    private findPreviousEditableColumn(cell: Element): Element {
        let prevCell: Element = cell.previousElementSibling;

        if (!prevCell) {
            const previousRow: Element = cell.parentElement.previousElementSibling;
            if (previousRow) {
                prevCell = previousRow.lastElementChild;
            }
        }

        if (prevCell) {
            return prevCell.classList.contains('editable-column') ? prevCell : this.findPreviousEditableColumn(prevCell);
        } else {
            return null;
        }
    }

    private findNextRowEditableColumn(row: Element, matColumnClassName: string): Element {
        const nextRow: Element = row.nextElementSibling;
        if (nextRow == null) {
            return null;
        }

        const column: Element = nextRow.getElementsByClassName(matColumnClassName)[ 0 ];
        return column != null && column.classList.contains('editable-column') ? column : this.findNextRowEditableColumn(nextRow, matColumnClassName);
    }

    private findCell(element: Element): Element {
        if (element) {
            let cell: Element = element;
            while (cell && !cell.classList.contains('editing-cell')) {
                cell = cell.parentElement;
            }

            return cell;
        } else {
            return null;
        }
    }

    private executeMove(event: KeyboardEvent, targetCell: Element): void {
        // If datepicker is opened, remove cdk-overlay from dom
        const element: Element = event.composedPath()[ 0 ] as Element;
        if (element.className.indexOf('td-input-date') > -1) {
            const cdkOverlayContainer: Element = document.getElementsByClassName('cdk-overlay-container')[ 0 ];
            const cdkBackdrop: Element = cdkOverlayContainer.getElementsByClassName('cdk-overlay-backdrop')[ 0 ];
            if (cdkBackdrop != null) {
                this.invokeElementMethod(cdkBackdrop, 'click');
            }
            cdkOverlayContainer.innerHTML = '';
        }

        this.invokeElementMethod(event.target, 'blur');
        if (targetCell != null) {
            this.invokeElementMethod(targetCell, 'click');
        }
        event.preventDefault();
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private invokeElementMethod(element: any, methodName: string, args?: any[]): void {
        // eslint-disable-next-line prefer-spread
        element[ methodName ].apply(element, args);
    }

}
